import type { CriminalCategory, CriminalProceeding } from './criminalTaxonomyHelpers';
import {
  CRIMINAL_COURTS as C,
  CRIMINAL_SIDES as S,
  SPECIAL_COURT_SUBTYPES,
} from './criminalTaxonomyHelpers';

const COURTS = C;
const SIDES = S;

const bailTabs = ['All', 'Bail Petition', 'FIR & Police Record', 'Applications', 'Orders', 'Miscellaneous'];
const trialTabs = ['All', 'FIR / Challan', 'Applications', 'Evidence', 'Statements', 'Orders & Judgments', 'Miscellaneous'];
const appealTabs = ['All', 'Memo of Appeal', 'Applications', 'Impugned Judgment / Order', 'Record', 'Orders & Judgments', 'Miscellaneous'];
const revisionTabs = ['All', 'Revision Petition', 'Applications', 'Impugned Order / Record', 'Orders & Judgments', 'Miscellaneous'];
const miscTabs = ['All', 'Application', 'Reply / Objections', 'Supporting Documents', 'Orders', 'Miscellaneous'];
const complaintTabs = ['All', 'Complaint', 'Preliminary Evidence', 'Applications', 'Orders', 'Miscellaneous'];
const hcTabs = ['All', 'Petition', 'FIR / Complaint', 'Applications', 'Orders & Record', 'Miscellaneous'];

function bailProceeding(
  partial: Omit<CriminalProceeding, 'sideOptions' | 'documentTabs' | 'sideRoleKeys'>,
): CriminalProceeding {
  return {
    ...partial,
    sideOptions: [...SIDES.bail],
    sideRoleKeys: ['petitioner', 'state', 'complainant'],
    documentTabs: bailTabs,
  } as CriminalProceeding;
}

export const CRIMINAL_CATEGORIES: CriminalCategory[] = [
  {
    id: 'Bail Matters',
    label: 'Bail Matters',
    description:
      'Bail proceedings including pre-arrest, post-arrest, protective, cancellation, and suspension of sentence.',
    proceedings: [
      bailProceeding({
        id: 'Bail Before Arrest (BBA)',
        label: 'Bail Before Arrest (BBA)',
        shortLabel: 'BBA',
        description: 'Pre-arrest bail before Sessions Court or High Court.',
        legalBasis: 'Section 498 CrPC',
        courtOptions: [COURTS.sessions, { ...COURTS.highCourt, recommended: true }, COURTS.custom],
        courtHelper: 'Pre-arrest bail is ordinarily filed before Sessions Court or High Court.',
        defaultStages: [
          'Drafting / Filing',
          'Interim Relief',
          'Notice to State / Complainant',
          'Bail Confirmation Hearing',
          'Confirmed / Dismissed',
        ],
        recommendedDocuments: ['Bail Before Arrest Application'],
        aiContextHints:
          'AI should understand FIR, offence sections, apprehension of arrest, interim bail, surety, and confirmation stage.',
      }),
      bailProceeding({
        id: 'Post-Arrest Bail (BA)',
        label: 'Post-Arrest Bail (BA)',
        shortLabel: 'Post-Arrest',
        description: 'Bail after arrest and custody.',
        legalBasis: 'Section 497 CrPC',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.custom],
        courtHelper: 'Post-arrest bail forum depends on offence, custody, and court seized of the matter.',
        defaultStages: ['Arrest / Custody', 'Bail Filing', 'Notice / Comments', 'Arguments', 'Allowed / Dismissed'],
        recommendedDocuments: ['Post-Arrest Bail Application'],
        aiContextHints: 'AI should understand custody status, FIR, offence, bail grounds, and surety.',
      }),
      bailProceeding({
        id: 'Protective / Transit Bail',
        label: 'Protective / Transit Bail',
        shortLabel: 'Protective',
        description: 'Protective or transit bail to approach the competent court.',
        legalBasis: 'Section 498 CrPC',
        courtOptions: [{ ...COURTS.highCourt, recommended: true }, COURTS.sessions, COURTS.custom],
        courtHelper: 'Protective bail is time-bound relief to approach the competent court.',
        defaultStages: [
          'Filing',
          'Interim Protection',
          'Time-bound Relief',
          'Approach Competent Court',
          'Disposed',
        ],
        recommendedDocuments: ['Protective Bail Application'],
        aiContextHints: 'AI should understand FIR, apprehension of arrest, transit need, and protection period.',
      }),
      bailProceeding({
        id: 'Bail Confirmation',
        label: 'Bail Confirmation',
        shortLabel: 'Confirmation',
        description: 'Confirmation of interim pre-arrest bail.',
        legalBasis: 'Section 498 CrPC',
        courtOptions: [COURTS.sameCourtInterim, COURTS.highCourt, COURTS.sessions],
        defaultStages: [
          'Drafting / Filing',
          'Interim Relief',
          'Notice to State / Complainant',
          'Bail Confirmation Hearing',
          'Confirmed / Dismissed',
        ],
        recommendedDocuments: ['Bail Confirmation Application'],
        aiContextHints: 'AI should understand interim bail order, surety, and confirmation hearing.',
      }),
      bailProceeding({
        id: 'Bail Cancellation',
        label: 'Bail Cancellation',
        shortLabel: 'Cancellation',
        description: 'Cancellation of bail already granted.',
        legalBasis: 'Sections 497(5), 498 CrPC',
        courtOptions: [COURTS.sameCourt, COURTS.sessions, COURTS.highCourt],
        defaultStages: ['Application Filed', 'Notice', 'Comments', 'Arguments', 'Order'],
        recommendedDocuments: ['Bail Cancellation Application'],
        aiContextHints: 'AI should understand existing bail order, grounds for cancellation, and misuse/abscondence.',
      }),
      bailProceeding({
        id: 'Bail After Conviction / Suspension of Sentence',
        label: 'Bail After Conviction / Suspension of Sentence',
        shortLabel: 'After Conviction',
        description: 'Suspension of sentence and bail after conviction pending appeal.',
        legalBasis: 'Section 426 CrPC',
        courtOptions: [COURTS.appellate, COURTS.highCourt],
        defaultStages: [
          'Conviction',
          'Appeal Filed',
          'Suspension Application',
          'Hearing',
          'Suspension Granted / Refused',
        ],
        recommendedDocuments: ['Suspension of Sentence Application'],
        aiContextHints: 'AI should understand conviction, sentence, appeal, and suspension grounds.',
      }),
    ],
  },
  {
    id: 'Criminal Trial',
    label: 'Criminal Trial',
    description:
      'Criminal trial proceedings before Sessions Court, Magistrate, complaint courts, or special statute courts.',
    proceedings: [
      {
        id: 'Sessions Trial',
        label: 'Sessions Trial',
        shortLabel: 'Sessions',
        description:
          'Criminal trial before the Court of Sessions. Sessions triable offences and challan cases committed to Sessions Court.',
        legalBasis: 'Sections 265-C, 265-D CrPC',
        courtOptions: [COURTS.sessions],
        sideOptions: [...SIDES.stateTrial],
        sideRoleKeys: ['state', 'accused'],
        documentTabs: trialTabs,
        defaultStages: [
          'FIR / Investigation',
          'Challan Submitted',
          'Charge / Framing',
          'Prosecution Evidence',
          'Statement u/s 342 CrPC',
          'Defence Evidence',
          'Final Arguments',
          'Judgment',
        ],
        recommendedDocuments: ['Memo of Appearance'],
        aiContextHints:
          'AI should understand sessions triable offence, charge, prosecution evidence, 342 statement, and judgment.',
      },
      {
        id: 'Magistrate Trial',
        label: 'Magistrate Trial',
        shortLabel: 'Magistrate',
        description:
          'Criminal trial before Judicial Magistrate. Magistrate triable offences.',
        legalBasis: 'Sections 241-A, 242 CrPC',
        courtOptions: [COURTS.magistrate],
        sideOptions: [...SIDES.stateTrial],
        sideRoleKeys: ['state', 'accused'],
        documentTabs: trialTabs,
        defaultStages: [
          'FIR / Investigation',
          'Challan Submitted',
          'Charge / Notice',
          'Evidence',
          'Statement of Accused',
          'Arguments',
          'Judgment',
        ],
        recommendedDocuments: ['Memo of Appearance'],
        aiContextHints:
          'AI should understand magistrate triable offence, charge, evidence, statement of accused, and judgment.',
      },
      {
        id: 'Complaint Case',
        label: 'Complaint Case',
        shortLabel: 'Complaint',
        description:
          'Private criminal complaint proceedings. Defamation, cheque dishonour, cheating/fraud, breach of trust, and other private complaints.',
        legalBasis: 'Sections 190, 200–204 CrPC',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.custom],
        sideOptions: [...SIDES.complaintTrial],
        sideRoleKeys: ['complainant', 'accused'],
        documentTabs: trialTabs,
        defaultStages: [
          'Complaint Filed',
          'Preliminary Evidence',
          'Summoning of Accused',
          'Trial',
          'Judgment',
        ],
        recommendedDocuments: ['Criminal Complaint'],
        aiContextHints:
          'AI should understand complainant, accused, preliminary evidence, summoning, and trial stage.',
      },
      {
        id: 'Special Court Trial',
        label: 'Special Court Trial',
        shortLabel: 'Special Court',
        description: 'Criminal proceedings before special statute courts.',
        legalBasis: 'Applicable special statute + relevant CrPC provisions',
        courtOptions: [COURTS.special],
        subtypes: [...SPECIAL_COURT_SUBTYPES],
        sideOptions: [...SIDES.stateTrial],
        sideRoleKeys: ['state', 'accused'],
        documentTabs: trialTabs,
        defaultStages: [
          'FIR / Complaint',
          'Investigation',
          'Challan / Reference Filed',
          'Charge',
          'Evidence',
          'Arguments',
          'Judgment',
        ],
        recommendedDocuments: ['Memo of Appearance'],
        aiContextHints:
          'AI should understand special law, special court, charge, evidence, and judgment.',
      },
      {
        id: 'Other Criminal Trial',
        label: 'Other Criminal Trial',
        shortLabel: 'Other Trial',
        description: 'Use where the matter does not fit the above categories.',
        courtOptions: [COURTS.custom],
        sideOptions: [...SIDES.other],
        sideRoleKeys: ['party_a', 'party_b'],
        documentTabs: trialTabs,
        defaultStages: [
          'Case Preparation',
          'Filing / Institution',
          'Evidence',
          'Arguments',
          'Judgment',
        ],
        recommendedDocuments: ['Memo of Appearance'],
        aiContextHints:
          'AI should understand forum, parties, charge or complaint, evidence, and judgment.',
      },
      {
        id: 'Police Challan / State Case',
        label: 'Police Challan / State Case',
        shortLabel: 'Challan',
        deprecated: true,
        description: 'FIR → investigation → police challan / state prosecution case.',
        legalBasis: 'Sections 173, 190, 241-A/242, 265-C/265-D CrPC',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.stateTrial],
        sideRoleKeys: ['state', 'accused'],
        documentTabs: trialTabs,
        defaultStages: [
          'FIR',
          'Investigation',
          'Challan Submitted',
          'Charge / Framing',
          'Prosecution Evidence',
          'Statement u/s 342 CrPC',
          'Defence Evidence',
          'Final Arguments',
          'Judgment',
        ],
        recommendedDocuments: ['Memo of Appearance'],
        aiContextHints:
          'AI should understand FIR, challan, charge, prosecution evidence, 342 statement, and judgment.',
      },
      {
        id: 'Complaint Case Trial',
        label: 'Complaint Case Trial',
        shortLabel: 'Complaint Trial',
        deprecated: true,
        description: 'Trial arising from a criminal complaint.',
        legalBasis: 'Sections 190, 200–204 CrPC',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.custom],
        sideOptions: [...SIDES.complaintTrial],
        sideRoleKeys: ['complainant', 'accused'],
        documentTabs: trialTabs,
        defaultStages: [
          'Complaint Filed',
          'Preliminary Evidence',
          'Summoning',
          'Trial',
          'Judgment',
        ],
        recommendedDocuments: ['Criminal Complaint'],
        aiContextHints:
          'AI should understand complainant, accused, preliminary evidence, and trial stage.',
      },
    ],
  },
  {
    id: 'Criminal Appeal',
    label: 'Criminal Appeal',
    description: 'Appeals against conviction, acquittal, sentence, or criminal orders.',
    proceedings: [
      {
        id: 'Appeal Against Conviction',
        label: 'Appeal Against Conviction',
        shortLabel: 'Conviction',
        description: 'Appeal against conviction.',
        legalBasis: 'Sections 410, 411, 417 CrPC',
        courtOptions: [COURTS.sessions, COURTS.highCourt, COURTS.specialAppellate, COURTS.custom],
        sideOptions: [...SIDES.appeal],
        sideRoleKeys: ['appellant', 'respondent'],
        documentTabs: appealTabs,
        defaultStages: ['Appeal Filed', 'Admission', 'Lower Court Record', 'Arguments', 'Judgment'],
        recommendedDocuments: ['Criminal Appeal'],
        aiContextHints: 'AI should understand conviction, impugned judgment, grounds, and sentence.',
      },
      {
        id: 'Appeal Against Acquittal',
        label: 'Appeal Against Acquittal',
        shortLabel: 'Acquittal',
        description: 'Appeal against acquittal (state/complainant).',
        legalBasis: 'Section 417 CrPC',
        courtOptions: [COURTS.sessions, COURTS.highCourt, COURTS.specialAppellate, COURTS.custom],
        sideOptions: [...SIDES.appeal],
        sideRoleKeys: ['appellant', 'respondent'],
        documentTabs: appealTabs,
        defaultStages: ['Appeal Filed', 'Admission', 'Lower Court Record', 'Arguments', 'Judgment'],
        recommendedDocuments: ['Criminal Appeal'],
        aiContextHints: 'AI should understand acquittal, leave if required, and grounds for interference.',
      },
      {
        id: 'Appeal Against Sentence',
        label: 'Appeal Against Sentence',
        shortLabel: 'Sentence',
        description: 'Appeal against sentence only or including conviction.',
        legalBasis: 'Sections 410, 411 CrPC',
        courtOptions: [COURTS.sessions, COURTS.highCourt, COURTS.specialAppellate, COURTS.custom],
        sideOptions: [...SIDES.appeal],
        sideRoleKeys: ['appellant', 'respondent'],
        documentTabs: appealTabs,
        defaultStages: ['Appeal Filed', 'Admission', 'Lower Court Record', 'Arguments', 'Judgment'],
        recommendedDocuments: ['Criminal Appeal'],
        aiContextHints: 'AI should understand sentence, mitigating circumstances, and prayer.',
      },
      {
        id: 'Jail Appeal',
        label: 'Jail Appeal',
        shortLabel: 'Jail Appeal',
        description: 'Jail appeal by convict in custody.',
        legalBasis: 'Section 410 CrPC',
        courtOptions: [COURTS.highCourt, COURTS.custom],
        sideOptions: [...SIDES.appeal],
        sideRoleKeys: ['appellant', 'respondent'],
        documentTabs: appealTabs,
        defaultStages: ['Appeal Filed', 'Admission', 'Lower Court Record', 'Arguments', 'Judgment'],
        recommendedDocuments: ['Criminal Appeal'],
        aiContextHints: 'AI should understand jail appeal, custody, conviction, and limitation.',
      },
      {
        id: 'Appeal Against Order',
        label: 'Appeal Against Order',
        shortLabel: 'Order',
        description: 'Appeal against an appealable criminal order.',
        legalBasis: 'Sections 410, 411 CrPC',
        courtOptions: [COURTS.sessions, COURTS.highCourt, COURTS.specialAppellate, COURTS.custom],
        sideOptions: [...SIDES.appeal],
        sideRoleKeys: ['appellant', 'respondent'],
        documentTabs: appealTabs,
        defaultStages: ['Appeal Filed', 'Admission', 'Lower Court Record', 'Arguments', 'Judgment'],
        recommendedDocuments: ['Criminal Appeal'],
        aiContextHints: 'AI should understand impugned order, appealability, and grounds.',
      },
    ],
  },
  {
    id: 'Criminal Revision',
    label: 'Criminal Revision',
    description: 'Revisional criminal jurisdiction against criminal findings, proceedings, or orders.',
    proceedings: [
      {
        id: 'Revision Against Order',
        label: 'Revision Against Order',
        shortLabel: 'Order',
        description: 'Revision against a criminal order.',
        legalBasis: 'Sections 435, 439 CrPC',
        courtOptions: [COURTS.sessions, COURTS.highCourt, COURTS.custom],
        sideOptions: [...SIDES.revision],
        sideRoleKeys: ['revisionist', 'respondent'],
        documentTabs: revisionTabs,
        defaultStages: ['Revision Filed', 'Notice', 'Record Summoned', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Criminal Revision Petition'],
        aiContextHints: 'AI should understand impugned order, revisional grounds, and record.',
      },
      {
        id: 'Revision Against Conviction',
        label: 'Revision Against Conviction',
        shortLabel: 'Conviction',
        description: 'Revision against conviction.',
        legalBasis: 'Sections 435, 439 CrPC',
        courtOptions: [COURTS.sessions, COURTS.highCourt, COURTS.custom],
        sideOptions: [...SIDES.revision],
        sideRoleKeys: ['revisionist', 'respondent'],
        documentTabs: revisionTabs,
        defaultStages: ['Revision Filed', 'Notice', 'Record Summoned', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Criminal Revision Petition'],
        aiContextHints: 'AI should understand conviction, illegality, and record appreciation.',
      },
      {
        id: 'Revision Against Acquittal',
        label: 'Revision Against Acquittal',
        shortLabel: 'Acquittal',
        description: 'Revision against acquittal.',
        legalBasis: 'Sections 435, 439 CrPC',
        courtOptions: [COURTS.sessions, COURTS.highCourt, COURTS.custom],
        sideOptions: [...SIDES.revision],
        sideRoleKeys: ['revisionist', 'respondent'],
        documentTabs: revisionTabs,
        defaultStages: ['Revision Filed', 'Notice', 'Record Summoned', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Criminal Revision Petition'],
        aiContextHints: 'AI should understand acquittal, revisional maintainability, and record.',
      },
      {
        id: 'Revision Against Sentence',
        label: 'Revision Against Sentence',
        shortLabel: 'Sentence',
        description: 'Revision against sentence.',
        legalBasis: 'Sections 435, 439 CrPC',
        courtOptions: [COURTS.sessions, COURTS.highCourt, COURTS.custom],
        sideOptions: [...SIDES.revision],
        sideRoleKeys: ['revisionist', 'respondent'],
        documentTabs: revisionTabs,
        defaultStages: ['Revision Filed', 'Notice', 'Record Summoned', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Criminal Revision Petition'],
        aiContextHints: 'AI should understand sentence, revisional grounds, and prayer.',
      },
      {
        id: 'Revision Against Discharge',
        label: 'Revision Against Discharge',
        shortLabel: 'Discharge',
        description: 'Revision against discharge of accused.',
        legalBasis: 'Sections 435, 439 CrPC',
        courtOptions: [COURTS.sessions, COURTS.highCourt, COURTS.custom],
        sideOptions: [...SIDES.revision],
        sideRoleKeys: ['revisionist', 'respondent'],
        documentTabs: revisionTabs,
        defaultStages: ['Revision Filed', 'Notice', 'Record Summoned', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Criminal Revision Petition'],
        aiContextHints: 'AI should understand discharge order, evidence, and revisional grounds.',
      },
    ],
  },
  {
    id: 'Criminal Miscellaneous',
    label: 'Criminal Miscellaneous',
    description: 'Connected criminal applications and standalone miscellaneous proceedings.',
    proceedings: [
      {
        id: 'Superdari',
        label: 'Superdari',
        shortLabel: 'Superdari',
        description: 'Application for superdari / interim custody of property.',
        legalBasis: 'Section 516-A CrPC',
        courtOptions: [COURTS.samePending, COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.misc],
        sideRoleKeys: ['applicant', 'respondent', 'state'],
        documentTabs: miscTabs,
        defaultStages: ['Application Preparation', 'Filing', 'Notice', 'Arguments', 'Order', 'Closed'],
        recommendedDocuments: ['Superdari Application'],
        aiContextHints: 'AI should understand seized property, superdari grounds, and surety if any.',
      },
      {
        id: 'Return of Property',
        label: 'Return of Property',
        shortLabel: 'Property',
        description: 'Application for return of property.',
        legalBasis: 'Section 517 CrPC',
        courtOptions: [COURTS.samePending, COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.misc],
        sideRoleKeys: ['applicant', 'respondent', 'state'],
        documentTabs: miscTabs,
        defaultStages: ['Application Preparation', 'Filing', 'Notice', 'Arguments', 'Order', 'Closed'],
        recommendedDocuments: ['Return of Property Application'],
        aiContextHints: 'AI should understand property details, seizure, and entitlement.',
      },
      {
        id: 'Exemption from Appearance',
        label: 'Exemption from Appearance',
        shortLabel: 'Exemption',
        description: 'Exemption from personal appearance.',
        legalBasis: 'Section 205 CrPC',
        courtOptions: [COURTS.samePending, COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.misc],
        sideRoleKeys: ['applicant', 'respondent', 'state'],
        documentTabs: miscTabs,
        defaultStages: ['Application Preparation', 'Filing', 'Notice', 'Arguments', 'Order', 'Closed'],
        recommendedDocuments: ['Exemption Application'],
        aiContextHints: 'AI should understand accused, reason for exemption, and pending case stage.',
      },
      {
        id: 'Recall Witness',
        label: 'Recall Witness',
        shortLabel: 'Recall',
        description: 'Application to recall a witness.',
        legalBasis: 'Section 540 CrPC',
        courtOptions: [COURTS.samePending, COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.misc],
        sideRoleKeys: ['applicant', 'respondent', 'state'],
        documentTabs: miscTabs,
        defaultStages: ['Application Preparation', 'Filing', 'Notice', 'Arguments', 'Order', 'Closed'],
        recommendedDocuments: ['Recall Witness Application'],
        aiContextHints: 'AI should understand witness, prior examination, and grounds for recall.',
      },
      {
        id: 'Further Investigation',
        label: 'Further Investigation',
        shortLabel: 'Investigation',
        description: 'Application for further investigation.',
        legalBasis: 'Section 173 CrPC',
        courtOptions: [COURTS.samePending, COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.misc],
        sideRoleKeys: ['applicant', 'respondent', 'state'],
        documentTabs: miscTabs,
        defaultStages: ['Application Preparation', 'Filing', 'Notice', 'Arguments', 'Order', 'Closed'],
        recommendedDocuments: ['Further Investigation Application'],
        aiContextHints: 'AI should understand investigation stage, lacunae, and prayer.',
      },
      {
        id: 'Transfer Application',
        label: 'Transfer Application',
        shortLabel: 'Transfer',
        description: 'Application for transfer of case.',
        legalBasis: 'Sections 526, 528 CrPC',
        courtOptions: [COURTS.samePending, COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.misc],
        sideRoleKeys: ['applicant', 'respondent', 'state'],
        documentTabs: miscTabs,
        defaultStages: ['Application Preparation', 'Filing', 'Notice', 'Arguments', 'Order', 'Closed'],
        recommendedDocuments: ['Transfer Application'],
        aiContextHints: 'AI should understand forum below, grounds for transfer, and prejudice.',
      },
      {
        id: 'Cancellation of Warrants',
        label: 'Cancellation of Warrants',
        shortLabel: 'Cancel Warrants',
        description: 'Application for cancellation of warrants.',
        legalBasis: 'Sections 204, 205 CrPC',
        courtOptions: [COURTS.samePending, COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.misc],
        sideRoleKeys: ['applicant', 'respondent', 'state'],
        documentTabs: miscTabs,
        defaultStages: ['Application Preparation', 'Filing', 'Notice', 'Arguments', 'Order', 'Closed'],
        recommendedDocuments: ['Cancellation of Warrants Application'],
        aiContextHints: 'AI should understand warrants, appearance, and grounds for cancellation.',
      },
      {
        id: 'Issuance of Warrants',
        label: 'Issuance of Warrants',
        shortLabel: 'Issue Warrants',
        description: 'Application for issuance of warrants.',
        legalBasis: 'Sections 204, 205 CrPC',
        courtOptions: [COURTS.samePending, COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.misc],
        sideRoleKeys: ['applicant', 'respondent', 'state'],
        documentTabs: miscTabs,
        defaultStages: ['Application Preparation', 'Filing', 'Notice', 'Arguments', 'Order', 'Closed'],
        recommendedDocuments: ['Issuance of Warrants Application'],
        aiContextHints: 'AI should understand accused absence, warrants sought, and stage of case.',
      },
      {
        id: 'Production Order',
        label: 'Production Order',
        shortLabel: 'Production',
        description: 'Application for production order.',
        legalBasis: 'Section 491 CrPC / applicable provisions',
        courtOptions: [COURTS.samePending, COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.misc],
        sideRoleKeys: ['applicant', 'respondent', 'state'],
        documentTabs: miscTabs,
        defaultStages: ['Application Preparation', 'Filing', 'Notice', 'Arguments', 'Order', 'Closed'],
        recommendedDocuments: ['Production Order Application'],
        aiContextHints: 'AI should understand custody, production need, and pending matter.',
      },
      {
        id: 'Other Criminal Application',
        label: 'Other Criminal Application',
        shortLabel: 'Other',
        description: 'Other connected or standalone criminal application.',
        legalBasis: 'Applicable CrPC / special law provisions',
        courtOptions: [COURTS.samePending, COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.misc],
        sideRoleKeys: ['applicant', 'respondent', 'state'],
        documentTabs: miscTabs,
        defaultStages: ['Application Preparation', 'Filing', 'Notice', 'Arguments', 'Order', 'Closed'],
        recommendedDocuments: ['Criminal Miscellaneous Application'],
        aiContextHints: 'AI should understand application type, pending forum, and relief sought.',
      },
    ],
  },
  {
    id: 'Private Complaint',
    label: 'Private Complaint',
    description: 'Private criminal complaints initiated directly before competent criminal courts.',
    proceedings: [
      {
        id: 'PPC Complaint',
        label: 'PPC Complaint',
        shortLabel: 'PPC',
        description: 'Private complaint under Pakistan Penal Code.',
        legalBasis: 'Sections 190, 200–204 CrPC',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.privateComplaint],
        sideRoleKeys: ['complainant', 'accused'],
        documentTabs: complaintTabs,
        defaultStages: ['Complaint Filed', 'Preliminary Evidence', 'Summoning', 'Trial', 'Judgment'],
        recommendedDocuments: ['Criminal Complaint'],
        aiContextHints: 'AI should understand complainant, accused, PPC sections, and preliminary evidence.',
      },
      {
        id: 'Defamation Complaint',
        label: 'Defamation Complaint',
        shortLabel: 'Defamation',
        description: 'Criminal defamation complaint.',
        legalBasis: 'PPC and Sections 190, 200–204 CrPC',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.custom],
        sideOptions: [...SIDES.privateComplaint],
        sideRoleKeys: ['complainant', 'accused'],
        documentTabs: complaintTabs,
        defaultStages: ['Complaint Filed', 'Preliminary Evidence', 'Summoning', 'Trial', 'Judgment'],
        recommendedDocuments: ['Criminal Complaint'],
        aiContextHints: 'AI should understand defamatory statement, publication, and complainant evidence.',
      },
      {
        id: 'Cheating / Fraud Complaint',
        label: 'Cheating / Fraud Complaint',
        shortLabel: 'Cheating',
        description: 'Complaint for cheating or fraud.',
        legalBasis: 'PPC and Sections 190, 200–204 CrPC',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.custom],
        sideOptions: [...SIDES.privateComplaint],
        sideRoleKeys: ['complainant', 'accused'],
        documentTabs: complaintTabs,
        defaultStages: ['Complaint Filed', 'Preliminary Evidence', 'Summoning', 'Trial', 'Judgment'],
        recommendedDocuments: ['Criminal Complaint'],
        aiContextHints: 'AI should understand cheating ingredients, documents, and witnesses.',
      },
      {
        id: 'Criminal Breach of Trust Complaint',
        label: 'Criminal Breach of Trust Complaint',
        shortLabel: 'CBT',
        description: 'Complaint for criminal breach of trust.',
        legalBasis: 'PPC and Sections 190, 200–204 CrPC',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.custom],
        sideOptions: [...SIDES.privateComplaint],
        sideRoleKeys: ['complainant', 'accused'],
        documentTabs: complaintTabs,
        defaultStages: ['Complaint Filed', 'Preliminary Evidence', 'Summoning', 'Trial', 'Judgment'],
        recommendedDocuments: ['Criminal Complaint'],
        aiContextHints: 'AI should understand entrustment, misappropriation, and evidence.',
      },
      {
        id: 'Dishonoured Cheque Complaint',
        label: 'Dishonoured Cheque Complaint',
        shortLabel: 'Cheque',
        description: 'Complaint for dishonoured cheque.',
        legalBasis: 'Negotiable Instruments Act / applicable law and CrPC',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.custom],
        sideOptions: [...SIDES.privateComplaint],
        sideRoleKeys: ['complainant', 'accused'],
        documentTabs: complaintTabs,
        defaultStages: ['Complaint Filed', 'Preliminary Evidence', 'Summoning', 'Trial', 'Judgment'],
        recommendedDocuments: ['Criminal Complaint'],
        aiContextHints: 'AI should understand cheque, dishonour, legal notice, and complainant evidence.',
      },
      {
        id: 'Other Complaint',
        label: 'Other Complaint',
        shortLabel: 'Other',
        description: 'Other private criminal complaint.',
        legalBasis: 'Sections 190, 200–204 CrPC',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.privateComplaint],
        sideRoleKeys: ['complainant', 'accused'],
        documentTabs: complaintTabs,
        defaultStages: ['Complaint Filed', 'Preliminary Evidence', 'Summoning', 'Trial', 'Judgment'],
        recommendedDocuments: ['Criminal Complaint'],
        aiContextHints: 'AI should understand offence, complainant, accused, and preliminary evidence.',
      },
    ],
  },
  {
    id: 'High Court Criminal Jurisdiction',
    label: 'High Court Criminal Jurisdiction',
    description:
      'Quashment, constitutional criminal matters, habeas corpus, and High Court criminal petitions.',
    proceedings: [
      {
        id: 'Quashment of FIR',
        label: 'Quashment of FIR',
        shortLabel: 'Quash FIR',
        description: 'Petition for quashing of FIR.',
        legalBasis: 'Section 561-A CrPC',
        courtOptions: [{ ...COURTS.highCourt, recommended: true }, COURTS.custom],
        sideOptions: [...SIDES.hcCriminal],
        sideRoleKeys: ['petitioner', 'respondent'],
        documentTabs: hcTabs,
        defaultStages: ['Petition Filed', 'Preliminary Hearing', 'Notice Issued', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Quashment Petition'],
        aiContextHints: 'AI should understand FIR contents, abuse of process, and maintainability.',
      },
      {
        id: 'Quashment of Proceedings',
        label: 'Quashment of Proceedings',
        shortLabel: 'Quash Proceedings',
        description: 'Petition for quashing criminal proceedings.',
        legalBasis: 'Section 561-A CrPC',
        courtOptions: [{ ...COURTS.highCourt, recommended: true }, COURTS.custom],
        sideOptions: [...SIDES.hcCriminal],
        sideRoleKeys: ['petitioner', 'respondent'],
        documentTabs: hcTabs,
        defaultStages: ['Petition Filed', 'Preliminary Hearing', 'Notice Issued', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Quashment Petition'],
        aiContextHints: 'AI should understand proceedings stage, abuse of process, and alternate remedy.',
      },
      {
        id: 'Quashment of Complaint',
        label: 'Quashment of Complaint',
        shortLabel: 'Quash Complaint',
        description: 'Petition for quashing private complaint.',
        legalBasis: 'Section 561-A CrPC',
        courtOptions: [{ ...COURTS.highCourt, recommended: true }, COURTS.custom],
        sideOptions: [...SIDES.hcCriminal],
        sideRoleKeys: ['petitioner', 'respondent'],
        documentTabs: hcTabs,
        defaultStages: ['Petition Filed', 'Preliminary Hearing', 'Notice Issued', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Quashment Petition'],
        aiContextHints: 'AI should understand complaint, preliminary evidence, and quashment grounds.',
      },
      {
        id: 'Quashment on Compromise',
        label: 'Quashment on Compromise',
        shortLabel: 'Compromise',
        description: 'Quashment petition on basis of compromise.',
        legalBasis: 'Section 561-A CrPC',
        courtOptions: [{ ...COURTS.highCourt, recommended: true }, COURTS.custom],
        sideOptions: [...SIDES.hcCriminal],
        sideRoleKeys: ['petitioner', 'respondent'],
        documentTabs: hcTabs,
        defaultStages: ['Petition Filed', 'Preliminary Hearing', 'Notice Issued', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Quashment Petition'],
        aiContextHints: 'AI should understand compromise, compoundable offence, and joint petition.',
      },
      {
        id: 'Constitutional Criminal Petition (199)',
        label: 'Constitutional Criminal Petition (199)',
        shortLabel: 'Art. 199',
        description: 'Constitutional petition in criminal jurisdiction.',
        legalBasis: 'Article 199 Constitution / Section 561-A CrPC',
        courtOptions: [{ ...COURTS.highCourt, recommended: true }, COURTS.custom],
        sideOptions: [...SIDES.hcCriminal],
        sideRoleKeys: ['petitioner', 'respondent'],
        documentTabs: hcTabs,
        defaultStages: ['Petition Filed', 'Preliminary Hearing', 'Notice Issued', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Constitutional Criminal Petition'],
        aiContextHints: 'AI should understand impugned action, constitutional grounds, and alternate remedy.',
      },
      {
        id: 'Habeas Corpus',
        label: 'Habeas Corpus',
        shortLabel: 'Habeas Corpus',
        description: 'Habeas corpus petition for illegal detention.',
        legalBasis: 'Section 491 CrPC / Article 199 Constitution',
        courtOptions: [{ ...COURTS.highCourt, recommended: true }, COURTS.custom],
        sideOptions: [...SIDES.hcCriminal],
        sideRoleKeys: ['petitioner', 'respondent'],
        documentTabs: hcTabs,
        defaultStages: ['Petition Filed', 'Preliminary Hearing', 'Notice Issued', 'Arguments', 'Final Order'],
        recommendedDocuments: ['Habeas Corpus Petition'],
        aiContextHints: 'AI should understand detention, authority, and illegal custody grounds.',
      },
    ],
  },
  {
    id: 'Other Criminal Matter',
    label: 'Other Criminal Matter',
    description: 'Other criminal proceedings not covered above.',
    proceedings: [
      {
        id: 'Custom Criminal Matter',
        label: 'Custom Criminal Matter',
        shortLabel: 'Custom',
        description: 'Custom criminal proceeding not listed above.',
        courtOptions: [COURTS.magistrate, COURTS.sessions, COURTS.highCourt, COURTS.special, COURTS.custom],
        sideOptions: [...SIDES.other],
        sideRoleKeys: ['party_a', 'party_b'],
        documentTabs: ['All', 'Documents', 'Applications', 'Orders', 'Miscellaneous'],
        defaultStages: [
          'Case Preparation',
          'Filing / Institution',
          'Notice / Summons / Report',
          'Reply / Response',
          'Proceedings',
          'Arguments / Hearing',
          'Order / Judgment',
          'Closed',
        ],
        recommendedDocuments: ['Criminal Application'],
        aiContextHints: 'AI should rely on user description, documents, forum, and offence sections.',
      },
    ],
  },
];
